/* Copyright (C) Okahu Inc 2023-2024. All rights reserved. */
import { isValidElement, ReactNode } from 'react';
import { buildKahuLinkPath, parseKahuHref } from '@/utils/kahu-link-helpers';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { kahuCopyInfoType } from 'types/kahu';

import { KahuResponseLink } from '../kahu-response-link';

function extractText(node: ReactNode): string {
  if (typeof node === 'string') return node;
  if (typeof node === 'number') return String(node);
  if (isValidElement(node)) return extractText(node.props.children);
  if (Array.isArray(node)) return node.map(extractText).join('');
  return '';
}

/**
 * Renders a single trace field value. The value is raw markdown, so a plain
 * string renders as text while a markdown link (e.g. the `Workflow` field)
 * renders as a clickable Kahu link through the same component the main
 * response uses. `p` is collapsed to a span so the value stays inline.
 */
export const TraceFieldValue = function ({
  value,
  occurrenceKey,
  copiedInfo,
  setCopiedInfo,
}: {
  value: string;
  occurrenceKey: string;
  copiedInfo: kahuCopyInfoType | null;
  setCopiedInfo: React.Dispatch<React.SetStateAction<kahuCopyInfoType | null>>;
}) {
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      urlTransform={(url) => url}
      components={{
        p: ({ children }) => (
          <span className="break-all text-body text-[#EBEFF1]">{children}</span>
        ),
        code: ({ children }) => (
          <code className="rounded bg-[#FFFFFF14] px-[3px] text-body font-normal text-body-primary">
            {children}
          </code>
        ),
        a: ({ children, href }) => {
          const path = buildKahuLinkPath(parseKahuHref(href ?? ''));
          return (
            <KahuResponseLink
              text={extractText(children)}
              href={path ?? href ?? '#'}
              occurrenceKey={occurrenceKey}
              copiedInfo={copiedInfo}
              setCopiedInfo={setCopiedInfo}
            />
          );
        },
      }}
    >
      {value}
    </ReactMarkdown>
  );
};
